import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FeedbackGateway } from '@prlw/core/feedback/feedback-gateway.interface';
import { FEEDBACK_GATEWAY } from '@prlw/core/feedback/feedback-gateway.token';
import { Feedback } from '@prlw/core/feedback/feedback.entity';
import { FileUploadService } from '@prlw/libs/file-upload/file-upload.service';
import { TUploadResult } from '@prlw/libs/file-upload/types';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';
import { EMPTY, map, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
  selector: 'prlw-feedback-input-ui',
  templateUrl: './feedback-input.component.html',
  styleUrls: ['./feedback-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FeedbackInputComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public themeInput: string = '';
  public messageInput: string = '';
  @Input() public isLoading: boolean = false;
  @Input() public allowedFileExtensions: string[] = [];
  @Output() public readonly closeModal = new EventEmitter<void>();
  @Output() public readonly sendFeedback = new EventEmitter<Feedback>();

  @ViewChild('fileInput', { static: false })
  public fileInput: ElementRef | undefined;

  constructor(
    private readonly fileUploadService: FileUploadService,
    private readonly responsiveService: ResponsiveService,
    @Inject(FEEDBACK_GATEWAY) private readonly feedbackGateway: FeedbackGateway,
  ) {
    this.eventAttachFile
      .pipe(
        switchMap((event) => {
          const file = (event.target as HTMLInputElement).files?.[0];
          if (!file) {
            return EMPTY;
          }
          return this.fileUploadService.attachFile(file, (formData) =>
            feedbackGateway.fileUpload(formData),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  attachedFiles$ = this.fileUploadService.attachedFiles$;
  thereArePendingAttachedFiles$ = this.attachedFiles$.pipe(
    map((attachedFiles) =>
      attachedFiles.some((attachFile) => attachFile.pending),
    ),
  );

  xsmall$ = this.responsiveService.xsmall$;

  public exit(): void {
    this.closeModal.emit();
  }

  public send(): void {
    if (this.themeInput) {
      if (this.messageInput) {
        this.sendFeedback.emit({
          themeInput: this.themeInput,
          messageInput: this.messageInput,
          attachedFiles: Object.values(this.fileUploadService.files$.getValue())
            .filter((item) => 'data' in item)
            .map((item) => item.data)
            .filter((item): item is TUploadResult => !!item),
        });
      }
    }
  }

  public onSelectFile(): void {
    this.fileInput?.nativeElement.click();
  }

  public get acceptedFileExtensions(): string {
    return this.allowedFileExtensions.map((ext) => `.${ext}`).join(',');
  }

  eventAttachFile = new EventEmitter<Event>();
}
